/**
 * TELV1-186 — A small pill that surfaces the project type next to a
 * project title. Used on the workspace project list, the project
 * detail header, and anywhere else a project is referenced in the
 * dashboard so the embed vs interview distinction stays visible after
 * the creation modal.
 *
 * For interview projects the pill is OMITTED (interview is the
 * default — no need to clutter the UI with a generic "Interview"
 * label everywhere). For embed projects, the pill is a small purple
 * Code-icon badge.
 *
 * Future project types: add a row in projectTypeConfig.ts and adjust
 * the showPill predicate below.
 */
import type { ProjectType } from '@/types';
import { cn } from '@/lib/utils';
import { getProjectTypeConfig } from '@/lib/projectTypeConfig';
import useTranslation from '@/hooks/useTranslation';

interface ProjectTypePillProps {
  projectType: ProjectType | undefined;
  /** Optional extra classes for layout (e.g. ml-2). */
  className?: string;
  /** Size variant; defaults to compact for in-line use next to titles. */
  size?: 'sm' | 'md';
}

export function ProjectTypePill({ projectType, className, size = 'sm' }: ProjectTypePillProps) {
  const { lang } = useTranslation();
  const config = getProjectTypeConfig(projectType);

  // Interview is the default — don't show a pill for it. Only embed
  // (and any non-default future types) get a visible marker.
  if (config.kind === 'interview') return null;

  const Icon = config.icon;
  const sizeClasses =
    size === 'sm' ? 'text-[10px] px-1.5 py-0.5 gap-1' : 'text-xs px-2 py-1 gap-1.5';
  const iconSize = size === 'sm' ? 11 : 13;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium whitespace-nowrap',
        sizeClasses,
        config.pillClassName,
        className,
      )}
      data-testid={`project-type-pill-${config.kind}`}
    >
      <Icon size={iconSize} aria-hidden="true" />
      {lang.get(config.pillLabelKey)}
    </span>
  );
}
